import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { MARCUS_DROP_MIME, type DroppedItem } from '~~/shared/documentDrop'
import type { ItemRow } from '~~/shared/items/itemRow'
import { fullPathOf } from '~~/shared/items/itemPath'
import { setWith, setWithout, type EntityCache } from '~/stores/entityCache'

export interface DocumentSelectionDeps {
  cache: EntityCache<ItemRow>
  /** The flat listing, uploads on top — the default navigation domain in folder mode. */
  items: ComputedRef<ItemRow[]>
  /** The server rows of the current folder — what a multi-row drag carries. */
  folderRows: ComputedRef<ItemRow[]>
  expandedFolders: Ref<Set<string>>
  getChildren: (folderId: string) => ItemRow[]
  moveItems: (ids: string[], destination: string) => Promise<void>
}

/**
 * Selection, keyboard navigation and drag-and-drop over the documents browser (MAR-948 split).
 *
 * These live in the store because they are shared BETWEEN surfaces (the sidebar and /app/docs select
 * the same documents). Per-surface display state — search filtering, group collapse — belongs in the
 * component that renders it.
 */
export function useDocumentSelection({ cache, items, folderRows, expandedFolders, getChildren, moveItems }: DocumentSelectionDeps) {
  const selectedIds = ref<Set<string>>(new Set())
  const selectionAnchorId = ref<string | null>(null)
  const dragItemId = ref<string | null>(null)
  const dropTargetId = ref<string | null>(null)

  /**
   * The flattened visible rows in display order: each top-level item, then recursively the children
   * of any expanded folder. The domain for range-selection and arrow-key navigation.
   *
   * In grouped mode the sidebar owns the ordering (it applies its own filter), so it passes its
   * flattened list to `setNavigationDomain`. Folder mode derives it here.
   */
  const navigationOverride = ref<string[] | null>(null)

  /**
   * DEDUPLICATED, because a domain is a SET of ids and not a rendering (AlexAmin/marcus#168).
   *
   * Since cross-source dedup was deleted, one document legitimately renders under two bands — once
   * under its sheet, once under its matter. The caller flattens what is on screen, so it hands the
   * same id in twice. `flatVisibleItems` would then resolve two identical rows, and arrow-key
   * navigation would stop on the document twice while range-select computed its span from indices
   * that no longer identify a row uniquely.
   *
   * Deduplicating HERE rather than in the caller keeps the one call site free to describe what it
   * renders, and covers any future caller for free.
   */
  function setNavigationDomain(ids: string[] | null) {
    navigationOverride.value = ids === null ? null : [...new Set(ids)]
  }

  const flatVisibleItems = computed<ItemRow[]>(() => {
    if (navigationOverride.value) return cache.resolve(navigationOverride.value)
    const out: ItemRow[] = []
    const walk = (list: ItemRow[]) => {
      for (const it of list) {
        out.push(it)
        if (it.type === 'folder' && expandedFolders.value.has(it.id)) walk(getChildren(it.id))
      }
    }
    walk(items.value)
    return out
  })

  const flatVisibleIds = computed<string[]>(() => flatVisibleItems.value.map(i => i.id))

  function selectOnly(id: string) {
    selectedIds.value = new Set([id])
    selectionAnchorId.value = id
  }

  function toggleSelected(id: string) {
    selectedIds.value = selectedIds.value.has(id) ? setWithout(selectedIds.value, id) : setWith(selectedIds.value, id)
    selectionAnchorId.value = id
  }

  /**
   * Select a contiguous range from the stable anchor to the target over the flattened visible tree.
   * Replaces the selection (Finder-style) rather than unioning it.
   */
  function selectRange(targetId: string) {
    const ids = flatVisibleIds.value
    const anchorId = selectionAnchorId.value
    if (!anchorId || ids.indexOf(anchorId) === -1) return selectOnly(targetId)
    const fromIdx = ids.indexOf(anchorId)
    const toIdx = ids.indexOf(targetId)
    if (fromIdx === -1 || toIdx === -1) return selectOnly(targetId)
    // Keep the anchor stable so successive shift-clicks grow/shrink from the same origin.
    selectedIds.value = new Set(ids.slice(Math.min(fromIdx, toIdx), Math.max(fromIdx, toIdx) + 1))
  }

  function selectMany(ids: string[]) {
    selectedIds.value = new Set(ids)
    selectionAnchorId.value = ids.length > 0 ? ids[ids.length - 1]! : null
  }

  function clearSelection() {
    selectedIds.value = new Set()
    selectionAnchorId.value = null
  }

  /** Move the single-selection up/down the flattened visible tree. */
  function moveSelection(delta: 1 | -1) {
    const ids = flatVisibleIds.value
    if (ids.length === 0) return
    const current = selectionAnchorId.value && ids.includes(selectionAnchorId.value)
      ? selectionAnchorId.value
      : [...selectedIds.value].find(id => ids.includes(id)) ?? null
    const nextIdx = current == null
      ? (delta === 1 ? 0 : ids.length - 1)
      : Math.min(ids.length - 1, Math.max(0, ids.indexOf(current) + delta))
    const nextId = ids[nextIdx]
    if (nextId) selectOnly(nextId)
  }

  function focusedItem(): ItemRow | null {
    const id = selectionAnchorId.value ?? [...selectedIds.value][0] ?? null
    if (!id) return null
    return flatVisibleItems.value.find(i => i.id === id) ?? null
  }

  // ── Drag and drop ─────────────────────────────────────────────────────────────────────────────
  function startDrag(item: ItemRow, e: DragEvent) {
    // If the dragged row isn't part of the selection, make it the selection first so what is visibly
    // selected and what moves stay in sync.
    if (!selectedIds.value.has(item.id)) selectOnly(item.id)
    dragItemId.value = item.id
    e.dataTransfer!.effectAllowed = 'copyMove'
    const dragged = selectedIds.value.has(item.id)
      ? folderRows.value.filter(i => selectedIds.value.has(i.id))
      : [item]
    const serialize = (i: ItemRow): DroppedItem => ({
      id: i.id, type: i.type, name: i.name, file_name: i.file_name,
      contract_type: i.contract_type, path: i.path, slug: i.slug,
      has_identity_data: i.has_identity_data ?? null,
    })
    e.dataTransfer!.setData('text/plain', dragged.map(i => i.id).join(','))
    e.dataTransfer!.setData(MARCUS_DROP_MIME.items, JSON.stringify(dragged.map(serialize)))
    e.dataTransfer!.setData(MARCUS_DROP_MIME.item, JSON.stringify(serialize(item)))
  }

  function endDrag() {
    dragItemId.value = null
    dropTargetId.value = null
  }

  /** The ids that would move on a drop initiated from `dragItemId`. */
  function draggedIds(): string[] {
    const id = dragItemId.value
    if (!id) return []
    return selectedIds.value.has(id) ? [...selectedIds.value] : [id]
  }

  /**
   * Whether dropping the current drag selection onto `target` (a folder) is allowed: not the dragged
   * item itself, not inside the dragged selection, and not a folder into itself or a descendant.
   */
  function isValidFolderDrop(target: ItemRow): boolean {
    if (target.type !== 'folder') return false
    const ids = draggedIds()
    if (ids.length === 0) return true
    if (ids.includes(target.id)) return false
    const targetPath = fullPathOf(target)
    for (const id of ids) {
      const dragged = cache.get(id)
      if (!dragged) continue
      if (dragged.path === targetPath) return false // already directly under the target
      if (dragged.type === 'folder') {
        const draggedPath = fullPathOf(dragged)
        if (targetPath === draggedPath || targetPath.startsWith(`${draggedPath}/`)) return false
      }
    }
    return true
  }

  async function dropOnto(destination: string) {
    const ids = draggedIds()
    dragItemId.value = null
    dropTargetId.value = null
    if (ids.length === 0) return
    await moveItems(ids, destination)
  }

  /** Forget removed documents in the selection. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    if (selectionAnchorId.value && gone.has(selectionAnchorId.value)) selectionAnchorId.value = null
    selectedIds.value = new Set([...selectedIds.value].filter(id => !gone.has(id)))
  }

  return {
    selectedIds, selectionAnchorId, dragItemId, dropTargetId,
    flatVisibleItems, flatVisibleIds, setNavigationDomain,
    selectOnly, toggleSelected, selectRange, selectMany, clearSelection, moveSelection, focusedItem,
    startDrag, endDrag, dropOnto, isValidFolderDrop, prune,
  }
}
